import React, { useState, useEffect, useCallback } from 'react';
import { Target, Plus, Zap, Trash2 } from 'lucide-react';
import BBALoader from '../BBALoader';
import Modal from '../Modal';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getUserGoals,
  addUserGoal,
  updateUserGoal,
  deleteUserGoal,
} from '../../services/firebase/userGoalsService';

// Editable goals grid for a single department, keyed by the goals-owner uid.
// Renders each goal as a half-width tile (two per row) with no outer card or
// header — the section header lives in the parent (CRM Portal View). The
// add/edit modal mirrors the portal Goals page (src/pages/Goals.js): a hero
// Target input with an embedded unit picker, a Manual / Sync-from-JobTread
// toggle, and JobTread-linked metrics. Live values aren't fetched here — a
// linked goal's current value stays in sync via the client's own portal.

const UNIT_OPTIONS = [
  { value: 'usd', label: 'USD ($)' },
  { value: 'percent', label: 'Percentage (%)' },
  { value: 'number', label: 'Number' },
  { value: 'days', label: 'Days' },
  { value: 'months', label: 'Months' },
];

const UNIT_SHORT = { usd: '$', percent: '%', number: '#', days: 'days', months: 'mo' };

// Metrics a goal can sync its "current" value from (mirrors portal Goals.js).
const LINKED_METRICS = {
  revenue: { label: 'Revenue', window: 'This year', unit: 'usd', group: 'Sales' },
  projectsSold: { label: 'Projects Sold', window: 'This year', unit: 'number', group: 'Sales' },
  avgJobSize: { label: 'Average Job Size', window: 'This year', unit: 'usd', group: 'Sales' },
  closeRatio: { label: 'Close Ratio', window: 'This year', unit: 'percent', group: 'Sales' },
  leads_month: { label: 'Leads', window: 'This month', unit: 'number', group: 'Marketing' },
  leads_quarter: { label: 'Leads', window: 'This quarter', unit: 'number', group: 'Marketing' },
  leads_year: { label: 'Leads', window: 'This year', unit: 'number', group: 'Marketing' },
  gpm: { label: 'Gross Profit Margin', window: 'This year', unit: 'percent', group: 'Finance' },
  grossProfit: { label: 'Gross Profit', window: 'This year', unit: 'usd', group: 'Finance' },
  netMargin: { label: 'Net Margin', window: 'This year', unit: 'percent', group: 'Finance' },
  netProfit: { label: 'Net Profit', window: 'This year', unit: 'usd', group: 'Finance' },
};

const METRIC_GROUPS = ['Sales', 'Marketing', 'Finance'];

const formatValue = (value, unit) => {
  const n = Number(value) || 0;
  switch (unit) {
    case 'usd':
      return `$${Math.round(n).toLocaleString()}`;
    case 'percent':
      return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case 'days':
      return `${n.toLocaleString()} days`;
    case 'months':
      return `${n.toLocaleString()} months`;
    default:
      return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
};

const calcProgress = (actual, target) =>
  Number(target) > 0 ? Math.min(100, Math.round((Number(actual) / Number(target)) * 100)) : 0;

const inputClass =
  'w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400';
const labelClass =
  'block text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5';

const emptyForm = { metricName: '', unit: 'usd', target: '', actual: '', linkedMetric: '' };

const PortalGoalsGrid = ({ userId, department, editable = true }) => {
  const { darkMode } = useTheme();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null); // null = adding a new goal
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!userId) {
      setGoals([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const all = await getUserGoals(userId);
      setGoals(Array.isArray(all?.[department]) ? all[department] : []);
    } catch (error) {
      console.error('Error loading goals:', error);
      setGoals([]);
    } finally {
      setLoading(false);
    }
  }, [userId, department]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleLinkedMetricChange = (key) => {
    setForm((prev) => ({
      ...prev,
      linkedMetric: key,
      unit: key ? LINKED_METRICS[key].unit : prev.unit,
      metricName:
        key && !prev.metricName.trim()
          ? `${LINKED_METRICS[key].label} (${LINKED_METRICS[key].window})`
          : prev.metricName,
    }));
  };

  const openAdd = () => {
    setEditingGoal(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (goal) => {
    setEditingGoal(goal);
    setForm({
      metricName: goal.metricName || '',
      unit: goal.unit || 'usd',
      target: goal.target ?? '',
      actual: goal.actual ?? '',
      linkedMetric: goal.linkedMetric || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingGoal(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId || !form.metricName.trim() || saving) return;
    const linked = form.linkedMetric || null;
    const unit = linked ? LINKED_METRICS[linked].unit : form.unit;
    const payload = {
      metricName: form.metricName.trim(),
      description: '',
      unit,
      target: parseFloat(form.target) || 0,
      actual: parseFloat(form.actual) || 0,
      linkedMetric: linked,
    };
    setSaving(true);
    try {
      if (editingGoal) {
        await updateUserGoal(userId, department, editingGoal.id, payload);
        setGoals((prev) => prev.map((g) => (g.id === editingGoal.id ? { ...g, ...payload } : g)));
      } else {
        const newGoal = { id: Date.now(), ...payload };
        await addUserGoal(userId, department, newGoal);
        setGoals((prev) => [...prev, newGoal]);
      }
      closeModal();
    } catch (error) {
      console.error('Error saving goal:', error);
      alert('Error saving goal. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingGoal) return;
    if (!window.confirm('Delete this goal? This action cannot be undone.')) return;
    setSaving(true);
    try {
      await deleteUserGoal(userId, department, editingGoal.id);
      setGoals((prev) => prev.filter((g) => g.id !== editingGoal.id));
      closeModal();
    } catch (error) {
      console.error('Error deleting goal:', error);
      alert('Error deleting goal. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <BBALoader size={40} />
      </div>
    );
  }

  const linkedMeta = form.linkedMetric ? LINKED_METRICS[form.linkedMetric] : null;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {goals.map((goal) => {
          const progress = calcProgress(goal.actual, goal.target);
          return (
            <div
              key={goal.id}
              onClick={editable ? () => openEdit(goal) : undefined}
              className={`p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm transition-shadow ${
                editable ? 'cursor-pointer hover:shadow-md' : ''
              }`}
            >
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-1.5">
                  {goal.metricName}
                  {goal.linkedMetric && <Zap className="w-3.5 h-3.5 text-orange-500" />}
                </h3>
                {goal.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{goal.description}</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatValue(goal.target, goal.unit)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Target</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-red-600 dark:text-red-400">
                    {formatValue(goal.actual, goal.unit)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Current</div>
                </div>
                <div className="text-center">
                  <div
                    className={`text-xl font-bold ${
                      progress >= 100
                        ? 'text-green-600 dark:text-green-400'
                        : progress >= 70
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {progress}%
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Complete</div>
                </div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    progress >= 90 ? 'bg-green-500' : progress >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          );
        })}

        {editable && (
          <button
            type="button"
            onClick={openAdd}
            className="p-5 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 hover:border-red-500 hover:text-red-600 transition-colors min-h-[9rem]"
          >
            <Plus className="w-6 h-6 mb-2" />
            <span className="text-sm font-medium">Add Goal</span>
          </button>
        )}
      </div>

      {goals.length === 0 && !editable && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">No goals set yet.</div>
      )}

      {editable && (
        <Modal isOpen={modalOpen} onClose={closeModal} maxWidth="xl">
          <div className="max-h-[80vh] overflow-y-auto pr-1 -mr-1">
            <div className="flex flex-col items-center text-center mb-5">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingGoal ? 'Edit Goal' : 'New Goal'}
              </h2>
              {!editingGoal && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Set a target and choose how to track it
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Goal Name */}
              <div>
                <label className={labelClass}>Goal Name *</label>
                <input
                  type="text"
                  value={form.metricName}
                  onChange={(e) => setField('metricName', e.target.value)}
                  className={inputClass}
                  placeholder="e.g., Annual Revenue"
                  required
                />
              </div>

              {/* Target — hero input with embedded unit control */}
              <div>
                <label className={labelClass}>Target *</label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center pointer-events-none">
                    <Target className="w-5 h-5" />
                  </div>
                  <input
                    type="number"
                    value={form.target}
                    onChange={(e) => setField('target', e.target.value)}
                    className="w-full pl-16 pr-24 py-4 rounded-xl border-2 border-red-200 dark:border-red-800/60 bg-white dark:bg-gray-800 text-2xl font-bold text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-500"
                    placeholder="0"
                    min="0"
                    step="0.01"
                    required
                  />
                  {linkedMeta ? (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400 dark:text-gray-500">
                      {UNIT_SHORT[linkedMeta.unit]}
                    </span>
                  ) : (
                    <select
                      value={form.unit}
                      onChange={(e) => setField('unit', e.target.value)}
                      title="Unit"
                      className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                    >
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u.value} value={u.value}>{UNIT_SHORT[u.value]}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Current Value Tracking toggle */}
              <div>
                <label className={labelClass}>Current Value Tracking</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleLinkedMetricChange('')}
                    className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                      !form.linkedMetric
                        ? 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    Manual entry
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLinkedMetricChange(form.linkedMetric || 'revenue')}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                      form.linkedMetric
                        ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Zap className="w-4 h-4" />
                    Sync from JobTread
                  </button>
                </div>
              </div>

              {/* Linked metric picker + info, or manual current input */}
              {form.linkedMetric ? (
                <div>
                  <label className={labelClass}>JobTread Metric</label>
                  <select
                    value={form.linkedMetric}
                    onChange={(e) => handleLinkedMetricChange(e.target.value)}
                    className={inputClass}
                  >
                    {METRIC_GROUPS.map((group) => (
                      <optgroup key={group} label={group}>
                        {Object.entries(LINKED_METRICS)
                          .filter(([, m]) => m.group === group)
                          .map(([key, m]) => (
                            <option key={key} value={key}>
                              {m.label} — {m.window.toLowerCase()}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                  </select>
                  <div className="mt-2 flex items-start gap-2 rounded-lg bg-orange-50 dark:bg-orange-900/20 px-3 py-2.5 text-xs text-orange-700 dark:text-orange-300">
                    <Zap className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      This goal's current value syncs automatically from JobTread when the client opens
                      their portal goals.
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <label className={labelClass}>Current</label>
                  <input
                    type="number"
                    value={form.actual}
                    onChange={(e) => setField('actual', e.target.value)}
                    className={inputClass}
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-2">
                <div>
                  {editingGoal && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium shadow-sm transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : editingGoal ? 'Save Changes' : 'Add Goal'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </>
  );
};

export default PortalGoalsGrid;
